/**
 * StatForm: edit form for the `stat` block.
 *
 * data: { items: [{ value, label }] }
 *
 * Array editing: each stat is an editable row (value / label) with a Remove
 * control; an Add Stat button appends a fresh blank row. `value` is kept as a
 * string from the input: the renderer (StatBlock) accepts string or number,
 * and authored stats are commonly non-numeric ("3.2k", "50+").
 *
 * Handles a missing / non-array `items` by treating it as an empty list.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, Card } from 'react-bootstrap';

export default function StatForm({ data, onChange }) {
    const d = data || {};
    const items = Array.isArray(d.items) ? d.items : [];

    const updateItems = (nextItems) => {
        onChange({ ...d, items: nextItems });
    };

    const updateRow = (index, field, value) => {
        const next = items.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        );
        updateItems(next);
    };

    const addRow = () => {
        updateItems([...items, { value: '', label: '' }]);
    };

    const removeRow = (index) => {
        updateItems(items.filter((_, i) => i !== index));
    };

    return (
        <>
            {items.length === 0 && (
                <p className="text-muted small mb-3">
                    No stats yet. Add one below.
                </p>
            )}
            {items.map((item, index) => (
                <Card key={`stat-item-${index}`} className="mb-2">
                    <Card.Body className="p-3">
                        <Row className="g-2 align-items-end">
                            <Col md={4}>
                                <Form.Label className="small mb-1">
                                    Value
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    value={
                                        item.value === undefined ||
                                        item.value === null
                                            ? ''
                                            : item.value
                                    }
                                    onChange={(e) =>
                                        updateRow(
                                            index,
                                            'value',
                                            e.target.value
                                        )
                                    }
                                    placeholder="e.g. 325"
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Label className="small mb-1">
                                    Label
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    value={item.label || ''}
                                    onChange={(e) =>
                                        updateRow(
                                            index,
                                            'label',
                                            e.target.value
                                        )
                                    }
                                    placeholder="e.g. Organizations"
                                />
                            </Col>
                            <Col md={2} className="d-grid">
                                <Button
                                    type="button"
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removeRow(index)}
                                >
                                    Remove
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            ))}
            <Button
                type="button"
                variant="outline-primary"
                size="sm"
                onClick={addRow}
            >
                + Add Stat
            </Button>
        </>
    );
}

StatForm.propTypes = {
    data: PropTypes.object,
    onChange: PropTypes.func.isRequired,
};

StatForm.defaultProps = {
    data: {},
};
